import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Plus,
  Edit,
  Copy,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Eye,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContractTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/contracts/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (err) {
      console.error('خطأ في جلب القوالب:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplateStatus = async (templateId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/contracts/templates/${templateId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTemplates();
    } catch (err) {
      console.error('خطأ في تحديث الحالة:', err);
      alert('حدث خطأ في تحديث حالة القالب');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا القالب؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/contracts/templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTemplates();
    } catch (err) {
      console.error('خطأ في حذف القالب:', err);
      alert('حدث خطأ في حذف القالب');
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      const token = localStorage.getItem('token');
      const newTemplate = {
        ...template,
        name_ar: `${template.name_ar} (نسخة)`,
        name_en: `${template.name_en} (Copy)`,
        is_active: false
      };
      delete newTemplate.id;
      delete newTemplate.created_at;
      delete newTemplate.updated_at;
      delete newTemplate.created_by;
      
      await axios.post(
        `${BACKEND_URL}/api/contracts/templates`,
        newTemplate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTemplates();
    } catch (err) {
      console.error('خطأ في نسخ القالب:', err);
      alert('حدث خطأ في نسخ القالب');
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.name_en.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8A870] mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#C8A870] to-[#b8986a] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1e3a8a]">إدارة قوالب العقود</h1>
                <p className="text-gray-600">عرض وإدارة جميع قوالب العقود</p>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/admin/contracts/templates/new')}
              className="flex items-center gap-2 bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg"
            >
              <Plus className="w-5 h-5" />
              إضافة قالب جديد
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="relative">
            <Search className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="بحث بالاسم..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
            />
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">{template.name_ar}</h3>
                    <p className="text-white/80 text-sm">{template.name_en}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {template.is_active ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4">
                {template.description_ar && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {template.description_ar}
                  </p>
                )}

                {/* Type Badge */}
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    {template.contract_type}
                  </span>
                </div>

                {/* Fillable Fields Count */}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <FileText className="w-4 h-4" />
                  <span>{template.fillable_fields?.length || 0} حقل قابل للتعبئة</span>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => navigate(`/admin/contracts/templates/${template.id}/edit`)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    تعديل
                  </button>
                  
                  <button
                    onClick={() => duplicateTemplate(template)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    نسخ
                  </button>

                  <button
                    onClick={() => toggleTemplateStatus(template.id, template.is_active)}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      template.is_active
                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {template.is_active ? (
                      <>
                        <ToggleRight className="w-4 h-4" />
                        تعطيل
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-4 h-4" />
                        تفعيل
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => deleteTemplate(template.id)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    حذف
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg">لا توجد قوالب متاحة</p>
            <button
              onClick={() => navigate('/admin/contracts/templates/new')}
              className="mt-4 text-[#C8A870] hover:underline"
            >
              إضافة قالب جديد
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractTemplatesPage;
